import { isEmpty } from 'lodash-es';
import { AxiosResponse } from 'axios';
import { api } from 'utils';
import { Interfaces as IAddressLookup } from '@service-victoria/component-library/components/AddressLookup/props';
import { OptionType } from '@service-victoria/component-library/components/AutoComplete/props';

/**
 * API Response for retriving list of addresses matching search critiriea
 */
type GetAddressResponse = {
  /**
   * Number of records in response
   */
  totalRecords: number;
  /**
   * List of all matching address records
   */
  records: IAddressLookup.ApiShortAddress[];
};

export default class AddressService {
  /**
   * api request adapter object
   */
  private adapter: api.Adapter;
  private addressLookupPromise?: () => Promise<AxiosResponse<GetAddressResponse>>;
  private addressLookupTimeout?: NodeJS.Timeout;
  /**
   * Static constant of search endpoint
   */
  static searchUri = '/adapter/v1/cwp/address/addresses';

  constructor(baseURL: string) {
    this.adapter = new api.Adapter({
      baseURL,
      responseType: 'json',
      headers: {
        timeout: 60 * 10 * 1000,
        'content-type': 'application/json',
        accept: 'application/json'
      }
    });
    // This binding maintains the value of this inside these methods during future calls.
    this.getAddressDetails = this.getAddressDetails.bind(this);
    this.getAddressesLookup = this.getAddressesLookup.bind(this);
  }

  /**
   * Rertrieve a list of addresses
   * @param queryString string to search
   */
  async getAddressesLookup(queryString: string): Promise<OptionType[]> {
    this.addressLookupPromise = () => this.adapter.getValue<GetAddressResponse>(queryString);
    return new Promise(res => {
      if (this.addressLookupTimeout) {
        clearTimeout(this.addressLookupTimeout);
      }

      this.addressLookupTimeout = setTimeout(async () => {
        if (!this.addressLookupPromise) return;

        try {
          const response = await this.addressLookupPromise();
          res(
            response.data.records
              .filter(record => !isEmpty(record.id))
              .map(record => {
                return {
                  label: record.addressLine,
                  value: record.id
                };
              })
          );
        } catch (e) {
          res([]);
        }
      }, 200);
    });
  }

  /**
   * Retrieve the full details of the address
   * @param id unique id for the address
   */
  async getAddressDetails(id: string, country: string): Promise<IAddressLookup.ApiFullAddress> {
    const url = `${AddressService.searchUri}/${encodeURIComponent(id)}?country=${country}`;
    const response = await this.adapter.getValue<IAddressLookup.ApiFullAddress>(url);
    return response.data;
  }
}
